import type { ReactElement } from 'react';
import { ENGINE_LABEL, type TargetEngine } from '../lib/targetEngine';

/**
 * The engine glyph beside a target's name.
 *
 * Same contract as `Icon`: 16×16 viewBox, stroke-only, `currentColor`, so it inherits
 * the row's colour. Unlike `Icon` it is NOT `aria-hidden` — here the icon carries the
 * only statement of what the target is (a Postgres firewall vs a Valkey one reads
 * identically in text), so it is a `role="img"` with a real name, and the same name is
 * the hover `title` (AC-4).
 *
 * `data-engine` is on the wrapper deliberately: a test — and a person in devtools —
 * can count targets by engine on any of the four surfaces without knowing which
 * component drew them.
 */
const PATHS: Record<TargetEngine, ReactElement> = {
  // Atlas: the MongoDB leaf. Not another cylinder — Atlas is the one target that is not
  // a DO managed database, and the two must be tellable apart at a glance.
  atlas: (
    <>
      <path d="M8 1.6c2.4 2.3 3.6 4.5 3.6 6.6 0 2.4-1.4 4.3-3.6 5.6-2.2-1.3-3.6-3.2-3.6-5.6 0-2.1 1.2-4.3 3.6-6.6z" strokeLinejoin="round" />
      <path d="M8 4.4v10" strokeWidth="1.2" />
    </>
  ),
  // Postgres: the cylinder with an elephant-ear notch on the cap.
  pg: (
    <>
      <ellipse cx="8" cy="4" rx="5" ry="2.2" />
      <path d="M3 4v8c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2V4" strokeLinejoin="round" />
      <path d="M3 8.2c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2" strokeWidth="1.2" />
    </>
  ),
  // Valkey / Redis: stacked slabs — the in-memory store, deliberately not a cylinder.
  valkey: (
    <>
      <path d="M2.4 4.4L8 2l5.6 2.4L8 6.8 2.4 4.4z" strokeLinejoin="round" />
      <path d="M2.4 8L8 10.4 13.6 8" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M2.4 11.4L8 13.8l5.6-2.4" strokeWidth="1.2" strokeLinejoin="round" />
    </>
  ),
  // MySQL: the cylinder with the dolphin's dorsal fin over it.
  mysql: (
    <>
      <ellipse cx="8" cy="4.4" rx="5" ry="2.2" />
      <path d="M3 4.4v7.2c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2V4.4" strokeLinejoin="round" />
      <path d="M5.2 9.6c2.4 0 4-1.1 4.8-2.6" strokeWidth="1.2" />
    </>
  ),
  // Unknown: a plain cylinder with a question — "we have not scanned this", never a
  // guess at an engine (AC-5).
  unknown: (
    <>
      <ellipse cx="8" cy="4" rx="5" ry="2.2" />
      <path d="M3 4v8c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2V4" strokeLinejoin="round" />
      <path d="M6.8 8.4a1.3 1.3 0 112.1 1.05c-.5.35-.9.6-.9 1.15" strokeWidth="1.2" />
      <path d="M8 12.2v.1" strokeWidth="1.4" />
    </>
  ),
};

export function EngineIcon({
  engine,
  size = 15,
  className,
}: {
  engine: TargetEngine;
  size?: number;
  className?: string;
}) {
  const label = ENGINE_LABEL[engine] ?? ENGINE_LABEL.unknown;
  return (
    <span
      className={className ? `engine-icon ${className}` : 'engine-icon'}
      data-engine={engine}
      title={label}
      role="img"
      aria-label={label}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        aria-hidden="true"
        focusable="false"
      >
        {PATHS[engine] ?? PATHS.unknown}
      </svg>
    </span>
  );
}
